import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLocalizedText } from '../utils/helpers';
import { Badge } from '../components/ui/badge';
import { ServiceCard, CourseCard } from '../components/Cards';
import { ChevronRight, MapPin, Award, Heart, User, GraduationCap } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeacherDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [teacher, setTeacher] = useState(null);
  const [services, setServices] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const teacherRes = await axios.get(`${API_URL}/api/teachers/${id}`);
        const teacherData = teacherRes.data;
        setTeacher(teacherData);
        
        // Load services
        const servicesRes = await axios.get(`${API_URL}/api/services`, { params: { teacher_id: id } });
        setServices(servicesRes.data);
        
        // If teacher also has a school profile, load courses
        if (teacherData.has_school_profile && teacherData.school_id) {
          const coursesRes = await axios.get(`${API_URL}/api/courses`, { params: { school_id: teacherData.school_id } });
          setCourses(coursesRes.data);
        }
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) return <div className="text-center py-12">{t('common.loading')}</div>;
  if (!teacher) return <div className="text-center py-12">Profesional no encontrado</div>;

  const hasImage = teacher.images && teacher.images.length > 0;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 animate-fade-in" data-testid="teacher-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> {t('common.back')}
      </button>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden p-8">
        <div className="flex items-start gap-6 mb-6">
          <div className="w-24 h-24 rounded-full bg-cream-dark flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ backgroundColor: '#F5EFE6' }}>
            {hasImage ? (
              <img src={teacher.images[0]} alt={teacher.user_name} className="w-full h-full object-cover" />
            ) : (
              <User className="h-12 w-12 text-gold" style={{ color: '#D4A574' }} />
            )}
          </div>
          <div>
            <h1 className="font-serif text-3xl font-bold text-primary">{teacher.user_name}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge className="bg-purple-500 text-white">Profesional</Badge>
              {teacher.has_school_profile && <Badge className="bg-blue-500 text-white">Escuela</Badge>}
            </div>
            <div className="flex items-center text-secondary mt-2">
              <MapPin className="h-4 w-4 mr-1" />
              {teacher.location?.city}, {teacher.location?.country}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-cream rounded-lg p-4" style={{ backgroundColor: '#F5EFE6' }}>
            <Award className="h-5 w-5 text-gold mb-2" style={{ color: '#D4A574' }} />
            <p className="text-sm text-secondary">Experiencia</p>
            <p className="font-semibold">{teacher.experience_years} años</p>
          </div>
          <div className="bg-cream rounded-lg p-4" style={{ backgroundColor: '#F5EFE6' }}>
            <Heart className="h-5 w-5 text-gold mb-2" style={{ color: '#D4A574' }} />
            <p className="text-sm text-secondary">Servicios</p>
            <p className="font-semibold">{services.length}</p>
          </div>
          {courses.length > 0 && (
            <div className="bg-cream rounded-lg p-4" style={{ backgroundColor: '#F5EFE6' }}>
              <GraduationCap className="h-5 w-5 text-gold mb-2" style={{ color: '#D4A574' }} />
              <p className="text-sm text-secondary">Cursos</p>
              <p className="font-semibold">{courses.length}</p>
            </div>
          )}
        </div>

        <div className="mb-6">
          <h3 className="font-serif text-xl font-semibold mb-3">{t('teacher.bio')}</h3>
          <p className="text-secondary whitespace-pre-line">{getLocalizedText(teacher.bio, i18n.language)}</p>
        </div>

        {teacher.specialties?.length > 0 && (
          <div className="mb-6">
            <h3 className="font-serif text-xl font-semibold mb-3">{t('teacher.specialties')}</h3>
            <div className="flex flex-wrap gap-2">
              {teacher.specialties.map((s, i) => (
                <Badge key={i} variant="outline">{s}</Badge>
              ))}
            </div>
          </div>
        )}

        {teacher.certifications?.length > 0 && (
          <div className="mb-6">
            <h3 className="font-serif text-xl font-semibold mb-3">{t('teacher.certifications')}</h3>
            <ul className="list-disc list-inside text-secondary">
              {teacher.certifications.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Services Section */}
        {services.length > 0 && (
          <div className="mb-8">
            <h3 className="font-serif text-xl font-semibold mb-4 flex items-center gap-2">
              <Heart className="h-5 w-5 text-purple-500" />
              {t('profile.myServices')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((service) => (
                <ServiceCard key={service.id} service={service} language={i18n.language} />
              ))}
            </div>
          </div>
        )}

        {/* Courses Section - only shown if user has school profile */}
        {courses.length > 0 && (
          <div>
            <h3 className="font-serif text-xl font-semibold mb-4 flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-blue-500" />
              {t('profile.myCourses')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {courses.map((course) => (
                <CourseCard key={course.id} course={course} language={i18n.language} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
